// Content model for /contact — the support triage page.
//
// Most of this used to live in the support@ auto-reply, which only fired AFTER the user had
// given up on the site and written in. Moving it in front of the mailto is the whole point of
// the page: answer the question first, and when that fails, route it to the right place —
// email for anything account-shaped (refunds, payments, Gems, logins, bugs), Discord for
// anything craft-shaped (prompting, settings, results). Once this ships the auto-reply is
// redundant and should be turned off.
//
// Copy lives in translation.json under `support_*`; this file holds structure, links and the
// email templates only.
//
// Mailto bodies are deliberately English-only. They are a form the support team parses, and a
// localized checklist ("• 決済プラットフォーム:") would slow triage down for no user gain — the
// user fills the values in, they don't have to read prose.

const SUPPORT_EMAIL = 'support@promptchan.com'
const BUSINESS_EMAIL = 'admin@promptchan.com'

export const DISCORD_URL = 'https://discord.gg/cZuJFFb34e'

// The things support actually gets emailed about. The topic only picks the subject line — the
// user writes the message itself in the page, so nothing arrives as a half-filled form. The
// placeholder is the only prompting they get, and it's a hint rather than homework.
export const SUPPORT_TOPICS = [
  {
    id: 'refund',
    labelKey: 'support_topic_refund',
    subject: 'Refund request',
    placeholderKey: 'support_placeholder_refund',
  },
  {
    id: 'billing',
    labelKey: 'support_topic_billing',
    subject: 'Billing or subscription',
    placeholderKey: 'support_placeholder_billing',
  },
  {
    id: 'gems',
    labelKey: 'support_topic_gems',
    subject: 'Missing Gems or upgrade',
    placeholderKey: 'support_placeholder_gems',
  },
  {
    id: 'account',
    labelKey: 'support_topic_account',
    subject: 'Account access',
    placeholderKey: 'support_placeholder_account',
  },
  {
    id: 'bug',
    labelKey: 'support_topic_bug',
    subject: 'Bug report',
    placeholderKey: 'support_placeholder_bug',
  },
  {
    id: 'other',
    labelKey: 'support_topic_other',
    subject: 'Support request',
    placeholderKey: 'support_placeholder_other',
  },
]

export const getSupportTopic = (id) =>
  SUPPORT_TOPICS.find((topic) => topic.id === id) ||
  SUPPORT_TOPICS[SUPPORT_TOPICS.length - 1]

// Assembles the finished email. The user's message goes in verbatim; we append the details we'd
// otherwise have to ask for — without a uid we can't find the account, and without the user
// agent we can't tell a browser bug from a device one.
//
// Returned as parts, not just a mailto, because the send path forks: a plain mailto normally,
// or navigator.share() when there's a screenshot to carry (mailto links cannot hold files).
export const buildSupportEmail = (
  topicId,
  { uid, email, plan, message } = {},
) => {
  const topic = getSupportTopic(topicId)
  const subject = uid ? `[${topic.subject}] — ${uid}` : `[${topic.subject}]`

  const details = [
    uid && `User ID: ${uid}`,
    email && `Account: ${email}`,
    plan && `Plan: ${plan}`,
    typeof window !== 'undefined' && `Page: ${window.location.href}`,
    typeof navigator !== 'undefined' && `Browser: ${navigator.userAgent}`,
  ].filter(Boolean)

  const body = [
    (message || '').trim(),
    '',
    '',
    '--- please keep the lines below ---',
    ...details,
  ].join('\n')

  return {
    subject,
    body,
    mailto: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`,
  }
}

export const BUSINESS_MAILTO = `mailto:${BUSINESS_EMAIL}?subject=${encodeURIComponent(
  'Business enquiry',
)}`

// Actions rendered under an answer. `topic` turns the action into a pre-filled email link;
// `label` is a raw string for proper nouns that shouldn't be translated (App Store, Discord).
// `nsfwOnly` actions are dropped in safe mode, which is also where the Discord card is hidden.
export const SUPPORT_CATEGORIES = [
  {
    id: 'billing',
    emoji: '💳',
    titleKey: 'support_cat_billing',
    faqs: [
      {
        id: 'cancel',
        emoji: '🚫',
        qKey: 'support_q_cancel',
        aKey: 'support_a_cancel',
        actions: [
          { labelKey: 'settings', href: '/settings' },
          {
            labelKey: 'support_action_cancelProvider',
            href: 'https://centrohelp.eu',
            external: true,
          },
          { labelKey: 'support_action_email', topic: 'billing' },
        ],
      },
      {
        id: 'refund',
        emoji: '💸',
        qKey: 'support_q_refund',
        aKey: 'support_a_refund',
        actions: [
          {
            label: 'App Store',
            href: 'https://support.apple.com/en-us/HT204084',
            external: true,
          },
          {
            label: 'Google Play',
            href: 'https://support.google.com/googleplay/workflow/9813244?hl=en',
            external: true,
          },
          {
            label: 'NOWPayments',
            href: 'https://nowpayments.io/help/payments/common/refund-policy',
            external: true,
          },
          { labelKey: 'refundpolicy', href: '/refund' },
          { labelKey: 'support_action_email', topic: 'refund' },
        ],
      },
      {
        id: 'refundTime',
        emoji: '⌛',
        qKey: 'support_q_refundTime',
        aKey: 'support_a_refundTime',
        actions: [],
      },
      {
        id: 'planChange',
        emoji: '🔀',
        qKey: 'support_q_planChange',
        aKey: 'support_a_planChange',
        actions: [
          { labelKey: 'settings', href: '/settings' },
          { labelKey: 'support_action_email', topic: 'billing' },
        ],
      },
      {
        id: 'notUpgraded',
        emoji: '⏳',
        qKey: 'support_q_notUpgraded',
        aKey: 'support_a_notUpgraded',
        actions: [
          { labelKey: 'upgrade', href: '/upgrade' },
          { labelKey: 'support_action_email', topic: 'billing' },
        ],
      },
      {
        id: 'doubleCharge',
        emoji: '🔁',
        qKey: 'support_q_doubleCharge',
        aKey: 'support_a_doubleCharge',
        actions: [
          { labelKey: 'settings', href: '/settings' },
          { labelKey: 'support_action_email', topic: 'billing' },
        ],
      },
      {
        id: 'deletedCharged',
        emoji: '👻',
        qKey: 'support_q_deletedCharged',
        aKey: 'support_a_deletedCharged',
        actions: [
          {
            labelKey: 'support_action_cancelProvider',
            href: 'https://centrohelp.eu',
            external: true,
          },
          { labelKey: 'support_action_email', topic: 'billing' },
        ],
      },
      {
        id: 'noAccess',
        emoji: '🔐',
        qKey: 'support_q_noAccess',
        aKey: 'support_a_noAccess',
        actions: [
          {
            labelKey: 'support_action_cancelProvider',
            href: 'https://centrohelp.eu',
            external: true,
          },
          { labelKey: 'support_action_email', topic: 'account' },
        ],
      },
      {
        id: 'chargeback',
        emoji: '⚠️',
        qKey: 'support_q_chargeback',
        aKey: 'support_a_chargeback',
        actions: [{ labelKey: 'support_action_email', topic: 'billing' }],
      },
      {
        id: 'history',
        emoji: '🧾',
        qKey: 'support_q_history',
        aKey: 'support_a_history',
        actions: [{ labelKey: 'settings', href: '/settings' }],
      },
    ],
  },
  {
    id: 'gems',
    emoji: '💎',
    titleKey: 'support_cat_gems',
    faqs: [
      {
        id: 'gemsMissing',
        emoji: '📭',
        qKey: 'support_q_gemsMissing',
        aKey: 'support_a_gemsMissing',
        actions: [
          { labelKey: 'upgrade', href: '/upgrade' },
          { labelKey: 'support_action_email', topic: 'gems' },
        ],
      },
      {
        id: 'failedGems',
        emoji: '💥',
        qKey: 'support_q_failedGems',
        aKey: 'support_a_failedGems',
        actions: [{ labelKey: 'support_action_email', topic: 'gems' }],
      },
      {
        id: 'unusedGems',
        emoji: '🚯',
        qKey: 'support_q_unusedGems',
        aKey: 'support_a_unusedGems',
        actions: [{ labelKey: 'refundpolicy', href: '/refund' }],
      },
      {
        id: 'redeem',
        emoji: '🎟️',
        qKey: 'support_q_redeem',
        aKey: 'support_a_redeem',
        actions: [{ labelKey: 'settings', href: '/settings' }],
      },
      {
        id: 'freeGems',
        emoji: '🆓',
        qKey: 'support_q_freeGems',
        aKey: 'support_a_freeGems',
        actions: [
          { labelKey: 'upgrade', href: '/upgrade' },
          { labelKey: 'earnGems', href: '/earn-gems' },
          { labelKey: 'gems', href: '/gems' },
        ],
      },
    ],
  },
  {
    id: 'creating',
    emoji: '🎨',
    titleKey: 'support_cat_creating',
    faqs: [
      {
        id: 'notGenerating',
        emoji: '🌀',
        qKey: 'support_q_notGenerating',
        aKey: 'support_a_notGenerating',
        actions: [{ labelKey: 'support_action_email', topic: 'bug' }],
      },
      {
        id: 'quality',
        emoji: '✨',
        qKey: 'support_q_quality',
        aKey: 'support_a_quality',
        actions: [
          {
            labelKey: 'support_action_promptingGuide',
            href: '/articles/prompting-guide',
          },
          {
            label: 'Discord',
            href: DISCORD_URL,
            external: true,
            nsfwOnly: true,
          },
        ],
      },
      // Rewritten against the support inbox: the recurring version of this question isn't
      // "why can't I upload a photo", it's "I paid specifically to animate my own image".
      // Covers photo upload, outside images and custom models in one answer.
      {
        id: 'upload',
        emoji: '📤',
        qKey: 'support_q_upload',
        aKey: 'support_a_upload',
        actions: [
          { labelKey: 'support_action_terms', href: '/termsofservice' },
          {
            labelKey: 'contentmoderationpolicy',
            href: '/contentmoderationpolicy',
          },
        ],
      },
      // The "all my videos were deleted!" emails — nobody knew about the 24-hour window.
      {
        id: 'expiry',
        emoji: '⏱️',
        qKey: 'support_q_expiry',
        aKey: 'support_a_expiry',
        actions: [],
      },
      {
        id: 'characterChanged',
        emoji: '🪞',
        qKey: 'support_q_characterChanged',
        aKey: 'support_a_characterChanged',
        actions: [{ labelKey: 'support_action_email', topic: 'bug' }],
      },
      {
        id: 'banned',
        emoji: '🛑',
        qKey: 'support_q_banned',
        aKey: 'support_a_banned',
        actions: [
          {
            labelKey: 'contentmoderationpolicy',
            href: '/contentmoderationpolicy',
          },
        ],
      },
    ],
  },
  {
    id: 'account',
    emoji: '👤',
    titleKey: 'support_cat_account',
    faqs: [
      {
        id: 'login',
        emoji: '🔑',
        qKey: 'support_q_login',
        aKey: 'support_a_login',
        actions: [{ labelKey: 'support_action_email', topic: 'account' }],
      },
      {
        id: 'siteDown',
        emoji: '🌐',
        qKey: 'support_q_siteDown',
        aKey: 'support_a_siteDown',
        actions: [
          {
            labelKey: 'support_action_status',
            href: '/articles/promptchan-down',
          },
        ],
      },
      {
        id: 'missingContent',
        emoji: '🖼️',
        qKey: 'support_q_missingContent',
        aKey: 'support_a_missingContent',
        actions: [{ labelKey: 'support_action_email', topic: 'account' }],
      },
      {
        id: 'delete',
        emoji: '🗑️',
        qKey: 'support_q_delete',
        aKey: 'support_a_delete',
        actions: [{ labelKey: 'settings', href: '/settings' }],
      },
      {
        id: 'recover',
        emoji: '↩️',
        qKey: 'support_q_recover',
        aKey: 'support_a_recover',
        actions: [],
      },
      {
        id: 'privacy',
        emoji: '👁️',
        qKey: 'support_q_privacy',
        aKey: 'support_a_privacy',
        actions: [{ labelKey: 'upgrade', href: '/upgrade' }],
      },
    ],
  },
  {
    id: 'safety',
    emoji: '🛡️',
    titleKey: 'support_cat_safety',
    faqs: [
      {
        id: 'removal',
        emoji: '🚨',
        qKey: 'support_q_removal',
        aKey: 'support_a_removal',
        actions: [
          { labelKey: 'contentremoval', href: '/contentremoval' },
          {
            labelKey: 'contentmoderationpolicy',
            href: '/contentmoderationpolicy',
          },
        ],
      },
      {
        id: 'dmca',
        emoji: '©️',
        qKey: 'support_q_dmca',
        aKey: 'support_a_dmca',
        actions: [{ labelKey: 'dmcapolicy', href: '/dmcapolicy' }],
      },
    ],
  },
  {
    id: 'business',
    emoji: '🤝',
    titleKey: 'support_cat_business',
    faqs: [
      {
        id: 'api',
        emoji: '🔌',
        qKey: 'support_q_api',
        aKey: 'support_a_api',
        actions: [
          { labelKey: 'apiDocs', href: '/docs' },
          { labelKey: 'settings', href: '/settings' },
        ],
      },
      {
        id: 'affiliate',
        emoji: '👑',
        qKey: 'support_q_affiliate',
        aKey: 'support_a_affiliate',
        actions: [
          {
            labelKey: 'support_action_affiliate',
            href: 'https://aiffiliate.io/',
            external: true,
          },
          {
            labelKey: 'businessContact',
            href: BUSINESS_MAILTO,
            external: true,
          },
        ],
      },
    ],
  },
]
